import React, { useState } from 'react';
import { Calendar as CalendarIcon, Eye, CalendarDays, Users, CalendarRange } from 'lucide-react';

import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ukWithLocalize } from '@/lib/ukLocale';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { buildTeacherSlug, buildGroupSlug } from '@/lib/slug';

const ScheduleFilters = ({
  selectedDate,
  onDateChange,
  viewMode,
  ViewModes,
  selectedGroup,
  onGroupChange,
  allGroups,
  selectedTeacher,
  onTeacherChange,
  allTeachers,
  teacherViewScope,
  onTeacherViewScopeChange,
  TeacherViewScopes,
  selectedTeacherGroup,
  onTeacherGroupChange,
  selectedTeacherMonth,
  onTeacherMonthChange,
  teacherGroups,
}) => {
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [monthMenuOpen, setMonthMenuOpen] = useState(false);
  const navigate = useNavigate();

  const formattedDate = selectedDate
    ? new Intl.DateTimeFormat('uk-UA', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(new Date(selectedDate))
    : null;

  const dayOfWeek = selectedDate ? new Date(selectedDate).getDay() : null;
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  return (
    <div className="flex flex-col items-center space-y-4">
      {(viewMode !== ViewModes.BY_TEACHER ||
        (viewMode === ViewModes.BY_TEACHER && teacherViewScope === TeacherViewScopes.DAY)) && (
        <div className="flex items-center justify-center space-x-3">
          <CalendarIcon className="h-8 w-8 text-primary" />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="group w-auto text-lg md:text-xl py-3 px-6 border-2 border-primary/50 hover:border-primary transition"
              >
                {selectedDate ? (
                  <span
                    className={`${
                      isWeekend
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-black dark:text-white group-hover:text-white'
                    }`}
                  >
                    {formattedDate}
                  </span>
                ) : (
                  <span className="text-black group-hover:text-white">Оберіть дату</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <Calendar
                skin="panel"
                compactMobile
                mode="single"
                selected={selectedDate}
                onSelect={onDateChange}
                initialFocus
                locale={ukWithLocalize}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {viewMode === ViewModes.BY_GROUP && (
  <div className="flex flex-col items-center space-y-3 w-full max-w-3xl">
    <div className="grid grid-cols-4 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-2 w-full">
      {allGroups.map((group, index) => {
        return (
          <button
            key={group}
             onClick={() => {
   onGroupChange(group);
   navigate(`/group/${buildGroupSlug(group)}`);
 }}
            className={`px-3 py-2 rounded border ${
              selectedGroup === group
                ? 'bg-primary text-white'
                : 'bg-background hover:bg-muted'
            } ${index % 2 === 1 ? 'translate-y-2' : ''}`}
          >
            {group}
          </button>
        );
      })}
    </div>
  </div>
)}


      {viewMode === ViewModes.BY_TEACHER && (
        <div className="flex flex-col items-center space-y-3">
          <div className="grid grid-cols-2 gap-2 w-full max-w-3xl sm:grid-cols-3 md:grid-cols-4">
            {allTeachers.map((teacher, index) => {
              const slug = buildTeacherSlug(teacher, index + 1);
              return (
                <button
  key={teacher}
 onClick={() => {
   onTeacherChange(teacher);
   navigate(`/teacher/${slug}`);
 }}
  className={`px-3 py-2 rounded border ${

                    selectedTeacher === teacher
                      ? 'bg-primary text-white'
                      : 'bg-background hover:bg-muted'
                  } ${index % 2 === 1 ? 'translate-y-2' : ''}`}
                >
                  {teacher}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant={teacherViewScope === TeacherViewScopes.DAY ? 'default' : 'outline'}
              onClick={() => {
                onTeacherViewScopeChange(TeacherViewScopes.DAY);
                onTeacherMonthChange('');
              }}
            >
              <Eye className="mr-2 h-4 w-4" /> День
            </Button>


{/* Тиждень */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={teacherViewScope === TeacherViewScopes.WEEK ? 'default' : 'outline'}
                >
                  <CalendarRange className="mr-2 h-4 w-4" />
                  Тиждень
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="center">
                <div className="text-xs text-muted-foreground mb-2">
                  обери будь-який день потрібного тижня
                </div>
                <Calendar
                  skin="panel"
                  compactMobile
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => {
                    onDateChange(d);
                    onTeacherViewScopeChange(TeacherViewScopes.WEEK);
                    onTeacherMonthChange('');
                  }}
                  initialFocus
                  locale={ukWithLocalize}
                />
              </PopoverContent>
            </Popover>


            <div className="relative">
              <Button
                variant={teacherViewScope === TeacherViewScopes.MONTH ? 'default' : 'outline'}
                onClick={() => setMonthMenuOpen(!monthMenuOpen)}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedTeacherMonth ? `Місяць ${parseInt(selectedTeacherMonth)}` : "Місяць ▾"}
              </Button>
              {monthMenuOpen && (
                <div className="absolute top-full left-0 mt-1 bg-background dark:bg-gray-800 border border-muted rounded shadow-md z-10 min-w-[180px] p-2 grid grid-cols-3 gap-2">
                  {[...Array(12)].map((_, i) => {
                    const monthVal = String(i + 1).padStart(2, "0");
                    return (
                      <button
                        key={monthVal}
                        className="px-2 py-1 hover:bg-muted rounded text-center"
                        onClick={() => {
                          onTeacherViewScopeChange(TeacherViewScopes.MONTH);
                          onTeacherMonthChange(monthVal);
                          setMonthMenuOpen(false);
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              variant={teacherViewScope === TeacherViewScopes.YEAR ? 'default' : 'outline'}
              onClick={() => {
                onTeacherViewScopeChange(TeacherViewScopes.YEAR);
                onTeacherMonthChange('');
              }}
            >
              <CalendarDays className="mr-2 h-4 w-4" /> Рік
            </Button>

            {Array.isArray(teacherGroups) && teacherGroups.length > 0 && (
              <div className="relative">
                <Button variant="outline" onClick={() => setGroupMenuOpen(!groupMenuOpen)}>
                  <Users className="mr-2 h-4 w-4" />
                  {selectedTeacherGroup && selectedTeacherGroup.trim() !== ''
                    ? selectedTeacherGroup
                    : "Всі групи ▾"}
                </Button>
                {groupMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-background dark:bg-gray-800 border border-muted rounded shadow-md z-10 min-w-[120px]">
                    <div className="flex flex-col">
                      <button
                        className="px-3 py-2 hover:bg-muted text-left"
                        onClick={() => {
                          onTeacherGroupChange('');
                          setGroupMenuOpen(false);
                        }}
                      >
                        Всі групи
                      </button>
                      {teacherGroups.map(group => (
                        <button
                          key={group}
                          className="px-3 py-2 hover:bg-muted text-left"
                          onClick={() => {
                            onTeacherGroupChange(group);
                            setGroupMenuOpen(false);
                          }}
                        >
                          {group}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleFilters;
